import React, { useState, useEffect } from 'react';
import theme from './TagInput.module.scss';
import { getProjectTags } from '@/services/project-tags';
import { ProjectTag, ProjectTagType, TranslatableText, OptionalId } from '@/types';
import { components, createFilter } from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTag } from '@fortawesome/pro-light-svg-icons';
import Creatable from 'react-select/creatable';
import Select from 'react-select';
import { classnames } from '@/utils/html';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { useSelector } from 'react-redux';
import { IState } from '@/state/IState';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';

export interface TagInputProps {
    projectId?: number | null;
    onTagsChange: (tags: ProjectTag[]) => void;
    type?: ProjectTagType;
    className?: string;
    defaultValue?: ProjectTag[];
    value?: OptionalId<ProjectTag>[] | number[];
    placeholder?: TranslatableText;
    canCreate?: boolean;
}

const MultiValueLabel = (props) => {
    const note = props.data.labelNote;
    return (
        <div key={props.data.value} className={theme.tag}>
            <FontAwesomeIcon icon={faTag} />
            <components.MultiValueLabel {...props}>
                {props.data.label}
                {note && <span className={theme.labelNote}>({note})</span>}
            </components.MultiValueLabel>
        </div>
    );
};

const formatOptionLabel = (data: { label: string; labelNote?: string | null }) => (
    <>
        {data.label}
        {data.labelNote && <span className={theme.labelNote}>({data.labelNote})</span>}
    </>
);

export const TagInput: React.FC<TagInputProps> = ({
    projectId,
    onTagsChange,
    type = ProjectTagType.Room,
    className,
    defaultValue,
    value,
    placeholder = type === ProjectTagType.NewRoom ? <Trans>Select room</Trans> : <Trans>Select tags</Trans>,
    canCreate = false,
}) => {
    const [tags, setTags] = useState<ProjectTag[] | null>(null);

    //Why are we using redux here??
    const reduxProjectId = useSelector((state: IState) =>
        state.global.activeProject ? state.global.activeProject.id : null
    );

    const filter = createFilter({
        ignoreCase: true,
        ignoreAccents: true,
        matchFrom: 'any',
        stringify: (option) => `${option.label} ${(option.data as any)?.labelNote ?? ''}`.trim(),
        trim: true,
    });

    useEffect(() => {
        const load = async () => {
            if (projectId) {
                setTags(await getProjectTags(projectId, type));
            } else if (reduxProjectId) {
                setTags(await getProjectTags(reduxProjectId, type));
            } else {
                setTags([]);
            }
        };
        load();
    }, [projectId, reduxProjectId, type]);

    const handleChange = (newValue, actionMeta: any) => {
        if (!newValue || !Array.isArray(newValue)) {
            triggerHandler(onTagsChange, []);
        } else {
            triggerHandler(onTagsChange, newValue);
        }
    };

    let fixedValue: { id: number; value: number; label: string }[] = [];
    // Is number
    if (tags && value && value.length) {
        if ((value as ProjectTag[])[0].label === undefined) {
            fixedValue = (value as number[]).map((id) => {
                const foundTag = tags.find((t) => t.id === +id);
                return { ...foundTag!, value: foundTag!.id };
            });
            //Should be ProjectTag[] here
        } else if ((value as ProjectTag[])[0].label) {
            fixedValue = (value as any[]).map((t) => ({ ...t, value: t.id || t.value }));
        }
    }
    const tagOptions = (tags || []).map((t) => ({ ...t, value: t.id }));

    let placeholderText = placeholder;
    if (tags?.length === 0) {
        placeholderText = type === ProjectTagType.NewRoom ? t`No rooms created yet` : t`No tags created yet`;
    }
    const props = {
        classNamePrefix: 'r-s',
        className: classnames('react-select', className),
        components: { MultiValueLabel },
        formatOptionLabel,
        placeholder: placeholderText,
        onChange: handleChange,
        defaultValue: defaultValue ? defaultValue.map((pt) => ({ ...pt, value: pt.id })) : undefined,
        options: tagOptions,
        closeMenuOnSelect: false,
        isMulti: true,
        value: fixedValue,
        isDisabled: !tags,
        filterOption: filter,
    };

    return (
        <>
            {canCreate ? (
                <Creatable {...props} noOptionsMessage={() => t`No tags`} />
            ) : (
                <Select {...props} noOptionsMessage={() => t`No tags`} />
            )}
        </>
    );
};
